import { Hand, Maximize2, MousePointer2, Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

/**
 * ZoomControls — bottom-left floating pill. Owns:
 *   • Zoom −, Reset (fit), Zoom + buttons
 *   • Live % readout
 *   • Pan-tool toggle (Hand icon) — when active, click-drag on the canvas
 *     pans the image instead of starting a new box. Standard image-editor
 *     pattern (Photoshop H-tool / Figma hand). Wired via the panMode +
 *     onTogglePanMode props.
 *
 * Wired via callbacks from Annotator (which owns the transform + panMode
 * state and passes them down).
 */
interface ZoomControlsProps {
    isDarkMode?: boolean;
    /** Current scale factor as a fraction (1.0 = fit-to-view). */
    scale: number;
    panMode: boolean;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onReset: () => void;
    onTogglePanMode: () => void;
}

export default function ZoomControls({
    isDarkMode = false,
    scale,
    panMode,
    onZoomIn,
    onZoomOut,
    onReset,
    onTogglePanMode,
}: ZoomControlsProps) {
    const { t } = useTranslation('canvas');
    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const btnCls = cn(
        'rounded-full p-1.5 transition-colors duration-150',
        isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100',
    );

    const percent = Math.round(scale * 100);

    return (
        <div
            className={cn(
                'absolute bottom-4 left-4 z-20 flex items-center gap-1 rounded-full backdrop-blur-sm border shadow-lg px-2 py-1',
                isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200',
            )}
        >
            {/* Mode toggle — Edit (default; select / draw / resize / move) vs Pan
                (move the image). Click the active icon to stay in that mode;
                click the other to switch. */}
            <button
                type="button"
                onClick={() => { if (panMode) onTogglePanMode(); }}
                aria-pressed={!panMode}
                title={t('tool_edit', { defaultValue: 'Edit mode — select, draw, resize boxes (default)' })}
                className={cn(
                    'rounded-full p-1.5 transition-colors duration-150',
                    !panMode
                        ? (isDarkMode ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-50 text-primary-700')
                        : (isDarkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'),
                )}
            >
                <MousePointer2 className="h-4 w-4" />
            </button>
            <button
                type="button"
                onClick={() => { if (!panMode) onTogglePanMode(); }}
                aria-pressed={panMode}
                title={t('tool_pan', { defaultValue: 'Pan mode — drag the image (Space). Boxes are read-only.' })}
                className={cn(
                    'rounded-full p-1.5 transition-colors duration-150',
                    panMode
                        ? (isDarkMode ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-50 text-primary-700')
                        : (isDarkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'),
                )}
            >
                <Hand className="h-4 w-4" />
            </button>

            <span className={cn('w-px h-5 mx-1', isDarkMode ? 'bg-gray-700' : 'bg-gray-200')} aria-hidden="true" />

            <button type="button" className={btnCls} aria-label={t('zoom_out')} title={t('zoom_out')} onClick={onZoomOut}>
                <Minus className="h-4 w-4" />
            </button>
            <button type="button" className={btnCls} aria-label={t('reset_zoom')} title={t('reset_zoom')} onClick={onReset}>
                <Maximize2 className="h-4 w-4" />
            </button>
            <button type="button" className={btnCls} aria-label={t('zoom_in')} title={t('zoom_in')} onClick={onZoomIn}>
                <Plus className="h-4 w-4" />
            </button>
            <span className={cn('text-xs px-2 min-w-[40px] text-center tabular-nums', muted)}>
                {percent}%
            </span>
        </div>
    );
}
